import React, { useMemo, useState } from 'react';
import { type Task } from '../../../services/projectManagement/tasksService';
import type { ProjectManagementMember } from '../../../services/projectManagement/membersService';
import { getDisplayName } from '../../../services/projectManagement/membersService';
import type { ProjectSection } from '../../../services/projectManagement/pmSectionsApiService';

interface TaskListViewProps {
  tasks: Task[];
  sections?: ProjectSection[];
  memberMap: Record<string, ProjectManagementMember>;
  onTaskClick: (taskId: string) => void;
  onStatusChange?: (taskId: string, status: string) => void;
  groupBy?: GroupBy;
  onGroupByChange?: (v: GroupBy) => void;
  sortBy?: SortBy;
  onSortByChange?: (v: SortBy) => void;
  sortAsc?: boolean;
  onSortAscChange?: (v: boolean) => void;
}

export type GroupBy = 'status' | 'priority' | 'project' | 'section' | 'none';
export type SortBy = 'due_date' | 'priority' | 'created_at' | 'title';

const TaskListView: React.FC<TaskListViewProps> = ({
  tasks,
  sections,
  memberMap,
  onTaskClick,
  onStatusChange,
  groupBy: groupByProp,
  onGroupByChange,
  sortBy: sortByProp,
  onSortByChange,
  sortAsc: sortAscProp,
  onSortAscChange,
}) => {
  const [groupByInternal, setGroupByInternal] = useState<GroupBy>('status');
  const [sortByInternal, setSortByInternal] = useState<SortBy>('due_date');
  const [sortAscInternal, setSortAscInternal] = useState(true);

  const groupBy = groupByProp ?? groupByInternal;
  const setGroupBy = onGroupByChange ?? setGroupByInternal;
  const sortBy = sortByProp ?? sortByInternal;
  const setSortBy = onSortByChange ?? setSortByInternal;
  const sortAsc = sortAscProp ?? sortAscInternal;
  const setSortAsc = onSortAscChange ?? setSortAscInternal;

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🟠';
      case 'urgent': return '🔴';
      default: return '⚪';
    }
  };

  const getPriorityValue = (priority: string) => {
    switch (priority) {
      case 'low': return 1;
      case 'medium': return 2;
      case 'high': return 3;
      case 'urgent': return 4;
      default: return 0;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'backlog': return 'bg-gray-100 text-gray-800';
      case 'todo': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-purple-100 text-purple-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      case 'done': return 'bg-green-100 text-green-800';
      case 'blocked': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const multiplier = sortAsc ? 1 : -1;

      switch (sortBy) {
        case 'due_date':
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return multiplier * (new Date(a.due_date).getTime() - new Date(b.due_date).getTime());

        case 'priority':
          return multiplier * (getPriorityValue(b.priority) - getPriorityValue(a.priority));

        case 'created_at':
          return multiplier * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

        case 'title':
          return multiplier * a.title.localeCompare(b.title);

        default:
          return 0;
      }
    });
  }, [tasks, sortAsc, sortBy]);

  const groupedEntries = useMemo(() => {
    if (groupBy === 'none') {
      return [{ key: 'All Tasks', label: 'All Tasks', tasks: sortedTasks }];
    }

    if (groupBy === 'section') {
      const labelByKey = new Map<string, string>();
      const groups: Record<string, Task[]> = {};
      const orderedKeys: string[] = [];

      const sectionList = Array.isArray(sections) ? [...sections] : [];
      sectionList.sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0));

      sectionList.forEach((s) => {
        const key = `section:${s.id}`;
        orderedKeys.push(key);
        labelByKey.set(key, s.name);
        groups[key] = [];
      });

      const noKey = '__no_section__';
      labelByKey.set(noKey, 'No Section');
      groups[noKey] = [];

      sortedTasks.forEach((task) => {
        const key = task.section_id ? `section:${task.section_id}` : noKey;
        if (!groups[key]) {
          groups[key] = [];
          orderedKeys.push(key);
          labelByKey.set(key, task.section_name || 'Section');
        }
        groups[key].push(task);
      });

      const includeNoSection = groups[noKey].length > 0 || sectionList.length === 0;
      if (includeNoSection) {
        orderedKeys.push(noKey);
      }

      const uniq = orderedKeys.filter((k, idx) => orderedKeys.indexOf(k) === idx);
      return uniq.map((key) => ({
        key,
        label: labelByKey.get(key) || key,
        tasks: groups[key] || [],
      }));
    }

    const next: Record<string, Task[]> = {};

    sortedTasks.forEach(task => {
      let key = '';
      switch (groupBy) {
        case 'status':
          key = task.status;
          break;
        case 'priority':
          key = task.priority;
          break;
        case 'project':
          key = task.project_name || 'No Project';
          break;
        default:
          key = 'All Tasks';
          break;
      }
      if (!next[key]) {
        next[key] = [];
      }
      next[key].push(task);
    });
    return Object.entries(next).map(([key, groupTasks]) => ({ key, label: key, tasks: groupTasks }));
  }, [groupBy, sections, sortedTasks]);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-4">
        <div>
          <label className="mr-2 text-sm font-medium text-gray-700">Group by:</label>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            className="rounded border border-gray-300 px-3 py-1 text-sm"
          >
            <option value="none">None</option>
            <option value="status">Status</option>
            <option value="priority">Priority</option>
            <option value="project">Project</option>
            {sections !== undefined ? <option value="section">Section</option> : null}
          </select>
        </div>
        
        <div>
          <label className="mr-2 text-sm font-medium text-gray-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className="rounded border border-gray-300 px-3 py-1 text-sm"
          >
            <option value="due_date">Due Date</option>
            <option value="priority">Priority</option>
            <option value="created_at">Created Date</option>
            <option value="title">Title</option>
          </select>
        </div>

        <button
          onClick={() => setSortAsc(!sortAsc)}
          className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50"
        >
          {sortAsc ? '↑ Ascending' : '↓ Descending'}
        </button>

        <div className="ml-auto text-sm text-gray-600">
          {tasks.length} tasks
        </div>
      </div>

      {/* Task Groups */}
      {groupedEntries.map(({ key, label, tasks: groupTasks }) => (
        <div key={key} className="rounded-lg border border-gray-200 bg-white">
          {/* Group Header */}
          {groupBy !== 'none' && (
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
              <h3 className="font-semibold text-gray-900">
                {label} <span className="text-sm text-gray-500">({groupTasks.length})</span>
              </h3>
            </div>
          )}

          {/* Task List */}
          <div className="divide-y divide-gray-200">
            {groupTasks.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No tasks in this group</div>
            ) : (
              groupTasks.map(task => (
                <div
                  key={task.id}
                  onClick={() => onTaskClick(task.id)}
                  className="cursor-pointer p-4 transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox placeholder */}
                    <div className="mt-1">
                      <input
                        type="checkbox"
                        checked={task.status === 'done'}
                        onChange={(e) => {
                          e.stopPropagation();
                          onStatusChange?.(task.id, e.target.checked ? 'done' : 'todo');
                        }}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </div>

                    {/* Task Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        <h4 className="text-sm font-semibold text-gray-900 flex-1">
                          {task.title}
                        </h4>
                        <span className="text-lg">
                          {getPriorityIcon(task.priority)}
                        </span>
                      </div>

                      {task.description && (
                        <p className="mt-1 text-sm text-gray-600 line-clamp-1">
                          {task.description}
                        </p>
                      )}

                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                        {/* Status Badge */}
                        <span className={`rounded-full px-2 py-1 font-semibold ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>

                        {/* Project */}
                        {task.project_name && (
                          <span className="text-gray-600">
                            📁 {task.project_name}
                          </span>
                        )}

                        {/* Due Date */}
                        {task.due_date && (
                          <span className={`${
                            new Date(task.due_date) < new Date() && task.status !== 'done'
                              ? 'text-red-600 font-semibold'
                              : 'text-gray-600'
                          }`}>
                            📅 {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        )}

                        {/* Time Estimate */}
                        {task.estimated_time > 0 && (
                          <span className="text-gray-600">
                            ⏱️ {task.estimated_time}h
                          </span>
                        )}

                        {task.assignee_id && (
                          <span className="text-gray-600">
                            👤 {getDisplayName(memberMap[task.assignee_id] || null)}
                          </span>
                        )}

                        {/* Labels */}
                        {task.labels && task.labels.length > 0 && (
                          <div className="flex gap-1">
                            {task.labels.slice(0, 2).map((label, index) => (
                              <span
                                key={index}
                                className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700"
                              >
                                {label}
                              </span>
                            ))}
                            {task.labels.length > 2 && (
                              <span className="text-gray-500">
                                +{task.labels.length - 2}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Subtask count */}
                        {task.subtask_count && task.subtask_count > 0 && (
                          <span className="text-gray-600">
                            ✓ {task.subtask_count} subtasks
                          </span>
                        )}

                        {/* Comment count */}
                        {task.comment_count && task.comment_count > 0 && (
                          <span className="text-gray-600">
                            💬 {task.comment_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TaskListView;
